/**
 * Runtime build-freshness helper.
 *
 * - Loads `/version.json` once at startup → that's the build the user is
 *   *currently running* (since the JS bundle and version.json shipped
 *   together on the same deploy).
 * - Polls `/version.json?_t=<now>` every 5 minutes (cache-busted). If the
 *   `build_id` differs from the one we loaded at startup, a newer build
 *   has been deployed. The DeploymentFreshnessBanner picks this up via
 *   `subscribe()`.
 *
 * Pure browser code, no React deps, so it can also be imported from the
 * footer / login page / SW (anywhere we want a stamp).
 */

const POLL_MS = 5 * 60 * 1000; // 5 min
const VERSION_URL = '/version.json';

let _bootMeta = null;        // build the user is currently running
let _latestMeta = null;       // most recent build seen on the server
let _listeners = new Set();
let _pollHandle = null;

async function _fetchMeta(noCache = false) {
  try {
    const url = noCache ? `${VERSION_URL}?_t=${Date.now()}` : VERSION_URL;
    const res = await fetch(url, { cache: 'no-store', credentials: 'omit' });
    if (!res.ok) return null;
    const json = await res.json();
    if (json && typeof json.build_id === 'string') return json;
    return null;
  } catch (_) {
    return null;
  }
}

function _notify() {
  for (const cb of _listeners) {
    try { cb({ boot: _bootMeta, latest: _latestMeta }); } catch (_) {}
  }
}

/** Initialise on app startup. Idempotent. */
export async function initBuildMeta() {
  if (_bootMeta) return _bootMeta; // already inited
  _bootMeta = await _fetchMeta(false);
  _latestMeta = _bootMeta;
  _notify();
  if (_pollHandle == null && _bootMeta) {
    _pollHandle = setInterval(async () => {
      const next = await _fetchMeta(true);
      if (next && (!_latestMeta || next.build_id !== _latestMeta.build_id)) {
        _latestMeta = next;
        _notify();
      }
    }, POLL_MS);
  }
  return _bootMeta;
}

/** Synchronously read the boot meta (may be null until init resolves). */
export function getBootMeta() { return _bootMeta; }

/** Synchronously read the latest server-known meta. */
export function getLatestMeta() { return _latestMeta; }

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribe(cb) {
  _listeners.add(cb);
  if (_bootMeta) cb({ boot: _bootMeta, latest: _latestMeta });
  return () => _listeners.delete(cb);
}

/** True when a newer build is deployed than what's in this tab. */
export function isStale() {
  if (!_bootMeta || !_latestMeta) return false;
  return _bootMeta.build_id !== _latestMeta.build_id;
}

/** Format the build stamp for footer display: "build abc12345 · 27 Apr 07:30" */
export function formatStamp(meta) {
  if (!meta || !meta.commit) return '';
  let when = '';
  try {
    if (meta.built_at) {
      const d = new Date(meta.built_at);
      when = d.toLocaleString(undefined, {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      });
    }
  } catch (_) {}
  return when ? `build ${meta.commit} · ${when}` : `build ${meta.commit}`;
}
